import { useEffect, useState } from "react"
import moment from "moment"
import { Button, Flex, Pagination, Tag } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Card } from "ui/Card"
import DataTable from "ui/DataTable"
import DataTableResponsive from "ui/DataTableResponsive"
import { Text, Title } from "ui/Typography"
import { spacings } from "themes/variables"
import { useDevice } from "hooks/useDevice"
import { toaster } from "components/common/Toast"
import { numberToCurrency } from "helpers/string"
import { parseDateOnly } from "helpers/time"
import { downloadFile } from "helpers/files"
import { downloadPlatformFeeInvoicePdf, getPlatformFeeInvoices } from "store/platform-fees"
import { toCurrencyCode } from "helpers/powertranz"

const INVOICE_STATUS_COLORS = {
  draft: "default",
  finalized: "processing",
  paid: "success",
  failed: "error",
  void: "default"
}

const formatPeriod = (invoice) =>
  `${moment(parseDateOnly(invoice.periodStart)).format("MMM D")} – ${moment(parseDateOnly(invoice.periodEnd)).format("MMM D, YYYY")}`

// Monthly DS platform fee invoices, collected from the guide's billing card (spec "Collection").
const PlatformFeeStatements = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isMobile, isTablet } = useDevice()
  const { items: invoices, meta, loading: isLoading } = useSelector((state) => state.platformFees.invoices)
  const [page, setPage] = useState(1)
  const [downloadingId, setDownloadingId] = useState(null)

  useEffect(() => {
    dispatch(getPlatformFeeInvoices({ page }))
  }, [dispatch, page])

  const handleDownload = async (invoice) => {
    setDownloadingId(invoice.id)
    try {
      const pdf = await dispatch(downloadPlatformFeeInvoicePdf(invoice.id))
      downloadFile({ originFileObj: pdf, name: t("powertranz.fees.pdf_filename", { period: invoice.periodStart }) })
    } catch {
      toaster.error(t("powertranz.fees.download_failed"))
    } finally {
      setDownloadingId(null)
    }
  }

  const rows = invoices.map((invoice) => ({
    key: invoice.id,
    id: invoice.id,
    period: formatPeriod(invoice),
    // The API stores money as integer cents.
    total: numberToCurrency(invoice.totalCents / 100, toCurrencyCode(invoice.currency)),
    status: <Tag color={INVOICE_STATUS_COLORS[invoice.status] || "default"}>{t(`powertranz.fees.status.${invoice.status}`)}</Tag>,
    download: (
      <Button type="link" onClick={() => handleDownload(invoice)} loading={downloadingId === invoice.id}>
        {t("powertranz.fees.download")}
      </Button>
    )
  }))

  const columns = [
    { title: t("powertranz.fees.period"), dataIndex: "period", key: "period" },
    { title: t("powertranz.fees.total"), dataIndex: "total", key: "total" },
    { title: t("powertranz.fees.status_label"), dataIndex: "status", key: "status" },
    { title: "", dataIndex: "download", key: "download", align: "right" }
  ]

  const totalResults = meta?.total_results ?? meta?.total_count

  return (
    <Card>
      <Flex vertical gap={spacings.M}>
        <Flex vertical gap={spacings.XXS}>
          <Title level={5}>{t("powertranz.fees.title")}</Title>
          <Text>{t("powertranz.fees.description")}</Text>
        </Flex>
        {!isLoading && invoices.length === 0 && <Text>{t("powertranz.fees.empty")}</Text>}
        {invoices.length > 0 &&
          (isMobile || isTablet ? (
            <DataTableResponsive
              data={rows}
              fieldOrder={["period", "total", "status", "download"]}
              labelMap={{
                period: t("powertranz.fees.period"),
                total: t("powertranz.fees.total"),
                status: t("powertranz.fees.status_label"),
                download: ""
              }}
            />
          ) : (
            <DataTable columns={columns} dataSource={rows} loading={isLoading} pagination={false} rowKey="key" />
          ))}
        {totalResults > invoices.length && (
          <Pagination current={page} total={totalResults} pageSize={meta?.per_page || invoices.length} onChange={setPage} showSizeChanger={false} />
        )}
      </Flex>
    </Card>
  )
}

export default PlatformFeeStatements
